import { useEffect, useState } from "react";

/**
 * Returns Yiwu (Asia/Shanghai) day-of-week (0=Sun..6=Sat) and hour (0-23).
 */
function getYiwuTime() {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const day = dayMap[get("weekday")] ?? 1;
  const hour = parseInt(get("hour"), 10);
  const minute = parseInt(get("minute"), 10);
  return { day, hour, minute };
}

/**
 * Live = Mon (1) – Sat (6), 09:00 – 17:00 Asia/Shanghai.
 */
function isLiveNow() {
  const { day, hour } = getYiwuTime();
  return day >= 1 && day <= 6 && hour >= 9 && hour < 17;
}

function formatYiwuClock() {
  const { hour, minute } = getYiwuTime();
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * variant: 'compact' | 'full' | 'dark'
 */
export default function LiveStatus({ variant = "compact", className = "" }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const live = isLiveNow();
  const time = formatYiwuClock();
  const dot = live ? "#22C55E" : "#F59E0B";
  const label = live ? "Live now" : "Off hours";

  if (variant === "compact") {
    return (
      <span
        data-testid="live-status-compact"
        data-live={live ? "true" : "false"}
        className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] ${className}`}
        title={`${label} · Yiwu ${time}`}
      >
        <span className="relative inline-flex w-2 h-2">
          <span
            className={`absolute inset-0 rounded-full ${live ? "animate-ping" : ""}`}
            style={{ background: dot, opacity: live ? 0.6 : 0 }}
          />
          <span className="relative inline-block w-2 h-2 rounded-full" style={{ background: dot }} />
        </span>
        <span>{label}</span>
      </span>
    );
  }

  if (variant === "dark") {
    return (
      <div
        data-testid="live-status-dark"
        data-live={live ? "true" : "false"}
        className={`inline-flex items-center gap-3 px-3 py-2 border border-white/15 ${className}`}
      >
        <span className="relative inline-flex w-2.5 h-2.5">
          <span
            className={`absolute inset-0 rounded-full ${live ? "animate-ping" : ""}`}
            style={{ background: dot, opacity: live ? 0.65 : 0 }}
          />
          <span className="relative inline-block w-2.5 h-2.5 rounded-full" style={{ background: dot }} />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white">
          {live ? "Live in Yiwu" : "Off hours · Yiwu"}
        </span>
        <span className="font-mono text-[10px] tracking-[0.18em] text-white/55">{time} CST</span>
      </div>
    );
  }

  // full
  return (
    <div
      data-testid="live-status-full"
      data-live={live ? "true" : "false"}
      className={`inline-flex items-center gap-3 px-3 py-2 bg-white border border-ink-100 ${className}`}
    >
      <span className="relative inline-flex w-2.5 h-2.5">
        <span
          className={`absolute inset-0 rounded-full ${live ? "animate-ping" : ""}`}
          style={{ background: dot, opacity: live ? 0.6 : 0 }}
        />
        <span className="relative inline-block w-2.5 h-2.5 rounded-full" style={{ background: dot }} />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-900">
        {live ? "Live in Yiwu" : "Off hours · Yiwu"}
      </span>
      <span className="font-mono text-[10px] tracking-[0.18em] text-ink-500">{time} CST</span>
    </div>
  );
}
